import { Tabs, Row, Typography, Button, Spin, Image, Col, Input, InputNumber, List, Tooltip, Select, message } from "antd";
import { useEffect, useState, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import HTTPClient from "../../utils/HTTPClient";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment/moment";
import { MessageOutlined, FileImageOutlined, ExperimentOutlined, CloudUploadOutlined, DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
    MinChatUiProvider,
    MainContainer,
    MessageInput,
    MessageContainer,
    MessageList,
    MessageHeader
} from "../../external_libs/chat_ui/index.tsx";

const { TextArea } = Input;
const { Option } = Select;

const AB_AI_Assistant = () => {
    const navigate = useNavigate();
    const [aiAssistantData, setAiAssistantData] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);

    const [loading, setLoading] = useState(false);
    const [loadingChatResponse, setLoadingChatResponse] = useState(false);
    const [fileName, setFileName] = useState('');
    const [fullFilePath, setFullFilePath] = useState('');
    const [toggleScrollToBottom, setToggleScrollToBottom] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [prefix, setPrefix] = useState("39");
    const [phoneNumberLoading, setPhoneNumberLoading] = useState(false);
    const [savedPhoneNumber, setSavedPhoneNumber] = useState(null);

    const { t } = useTranslation();

    const isLargeScreen = useMediaQuery({
        query: "(min-width: 771px)",
    });


    const handlePrefixChange = (value) => {
        setPrefix(value);
    };

    const selectPrefix = (
        <Select defaultValue="39" style={{ width: 80 }} onChange={handlePrefixChange}>
            <Option value="39">Italy</Option>
            <Option value="33">France</Option>
            <Option value="44">UK</Option>
            <Option value="49">Germany</Option>
            <Option value="1">U.S.A</Option>
            <Option value="34">Spain</Option>
            <Option value="30">Greece</Option>
            <Option value="31">Netherlands</Option>
            <Option value="32">Belgium</Option>
            <Option value="351">Portugal</Option>
            <Option value="353">Ireland</Option>
            <Option value="355">Albania</Option>
            <Option value="356">Malta</Option>
            <Option value="357">Cyprus</Option>
            <Option value="358">Finland</Option>
            <Option value="359">Bulgaria</Option>
            <Option value="370">Lithuania</Option>
            <Option value="371">Latvia</Option>
            <Option value="372">Estonia</Option>
            <Option value="373">Moldova</Option>
            <Option value="374">Armenia</Option>
            <Option value="36">Hungary</Option>
            <Option value="40">Romania</Option>
            <Option value="41">Switzerland</Option>
            <Option value="43">Austria</Option>
            <Option value="45">Denmark</Option>
            <Option value="46">Sweden</Option>
            <Option value="47">Norway</Option>
            <Option value="48">Poland</Option>
            <Option value="51">Peru</Option>
            <Option value="52">Mexico</Option>
            <Option value="53">Cuba</Option>
            <Option value="54">Argentina</Option>
            <Option value="55">Brazil</Option>
            <Option value="56">Chile</Option>
            <Option value="57">Colombia</Option>
            <Option value="58">Venezuela</Option>
            <Option value="60">Malaysia</Option>
            <Option value="61">Australia</Option>
            <Option value="62">Indonesia</Option>
            <Option value="63">Philippines</Option>
            <Option value="64">New Zealand</Option>
            <Option value="65">Singapore</Option>
            <Option value="66">Thailand</Option>
            <Option value="82">Vietnam</Option>
            <Option value="84">Myanmar</Option>
            <Option value="90">Turkey</Option>
            <Option value="91">India</Option>
            <Option value="92">Pakistan</Option>
            <Option value="93">Afghanistan</Option>
            <Option value="94">Sri Lanka</Option>
            <Option value="81">Japan</Option>
            <Option value="86">China</Option>
        </Select>
    );

    const fetch = async () => {
        setLoading(true);
        HTTPClient.get("/ab_ai_assistant/").then((response) => {
            if (response?.data) {
                setAiAssistantData(response.data);
                setChatMessages(response.data.messages);
                setSavedPhoneNumber(response.data.saved_phone_number);
                setToggleScrollToBottom(!toggleScrollToBottom);
            }
            setLoading(false);
        }).catch((error) => {
            if (error.response?.status === 402) {
                navigate("/home");
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        fetch();
    }, []);

    const uploadFile = (file) => {
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = "image/*";
        fileInput.click();

        fileInput.addEventListener("change", (event) => {
            const selectedFile = event.target.files[0];
            setFileName(selectedFile.name);
            setFullFilePath(selectedFile)
        });
    };

    const handleNewMessage = (text) => {
        setLoadingChatResponse(true);
        setChatMessages([...chatMessages, { text, user: { id: "user", name: "User" } }]);
        setToggleScrollToBottom(!toggleScrollToBottom);
        // TODO: Add logic to handle file upload
        if (fileName) {
            const formData = new FormData();
            formData.append("file", fullFilePath);
            formData.append("prompt", text);
            HTTPClient.post("/ab_ai_studio/chat/attachment", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((response) => {
                if (response?.data) {
                    setChatMessages(response.data.messages);
                    setToggleScrollToBottom(!toggleScrollToBottom);
                }
            }).catch((error) => {
                console.log(error);
            }).finally(() => {
                setLoadingChatResponse(false);
            });
            return;
        }

        HTTPClient.post("/ab_ai_assistant/chat", { prompt: text }).then((response) => {
            if (response?.data) {
                setChatMessages(response.data);
                setToggleScrollToBottom(!toggleScrollToBottom);
            }
        }).catch((error) => {
            console.log(error);
        }).finally(() => {
            setLoadingChatResponse(false);
        });

    }

    const savePhoneNumber = () => {
        if (!phoneNumber) {
            message.error(t("ab_ai_assistant.phone_number.error"));
            return;
        }
        setPhoneNumberLoading(true);
        HTTPClient.post(
            "/ab_ai_assistant/phone_number",
            { phone_number: prefix + phoneNumber.toString() })
            .then((response) => {
                fetch();
                message.success(t("ab_ai_assistant.phone_number.success"));
            }
            ).catch((error) => {
                message.error(t("ab_ai_assistant.phone_number.error"));
            }).finally(() => {
                setPhoneNumberLoading(false);
            });
    };

    const chatComponent = (<>
        <Row style={{ maxHeight: "550px", marginTop: "25px" }}>
            <MinChatUiProvider theme="#8476f5">
                <MainContainer style={{ height: "450px" }} render>
                    <MessageContainer>
                        <MessageList
                            currentUserId="user"
                            showTypingIndicator={loadingChatResponse}
                            typingIndicatorContent="Appbox AI is thinking..."
                            toggleScrollToBottom={toggleScrollToBottom}
                            messages={[
                                {
                                    text: t("ab_ai_studio.main.chat.welcome"),
                                    user: {
                                        id: "ai",
                                        name: "AI",
                                    },
                                },
                                ...chatMessages
                            ]}
                        />
                        {fileName && <div style={{ backgroundColor: "#f3f4f6", width: "98.5%" }}>{fileName}</div>}
                        <MessageInput placeholder={t("ab_ai_studio.main.chat.input_placeholder")} showAttachButton={false} onSendMessage={(text) => handleNewMessage(text)} onAttachClick={uploadFile} />
                    </MessageContainer>
                </MainContainer>
            </MinChatUiProvider>
        </Row></>
    )


    return (<>
        <Row style={{ backgroundColor: "white", width: "100%", height: isLargeScreen ? "134px" : "180px", padding: "27px 34px" }}>
            <Row>
                <Typography.Text style={{ fontSize: "27px", fontWeight: "bold" }}>
                    {t("ab_ai_assistant.title")}
                </Typography.Text>
            </Row>
            <Row style={{ width: "100%", marginTop: "10px" }}>
                <Typography.Text style={{ fontSize: "20px" }}>{t("ab_ai_assistant.subtitle")}</Typography.Text>
            </Row>
        </Row>
        <Row style={{ padding: isLargeScreen ? "27px 34px" : "10px 10px" }}>

            {loading || !aiAssistantData ? (
                <div
                    style={{
                        width: "100%",
                        minHeight: "20vh",
                        overflow: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}>
                    <Spin />
                </div>
            ) : (
                <>
                    <Row style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Col span={isLargeScreen ? 17 : 24}>
                            <div style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)", backgroundColor: "white", width: "100%", height: "100%", padding: "22px 27px", borderRadius: "14px" }}>
                                <Row style={{ display: "flex", justifyContent: "center", alignItems: "center" }}><img src={"/AI.png"} style={{ width: "100px", height: "100px" }} /></Row>
                                <Row style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                    <Typography.Text style={{ fontSize: "24px", fontWeight: "bold" }}>{t("ab_ai_studio.main.title")} {aiAssistantData?.user_name}</Typography.Text>
                                </Row>
                                {chatComponent}
                            </div>
                        </Col>
                    </Row>
                    <Row style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", marginTop: "20px" }}>
                        <Col span={isLargeScreen ? 17 : 24}>
                            <div style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)", backgroundColor: "white", width: "100%", height: "100%", padding: "22px 27px", borderRadius: "14px" }}>
                                <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "20px" }}>
                                    <Typography.Text style={{ fontSize: "24px", fontWeight: "bold" }}>{t("ab_ai_assistant.phone_number.title")}</Typography.Text>
                                </Row>
                                {savedPhoneNumber && (
                                    <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "20px" }}>
                                        <Typography.Text style={{ fontSize: "20px" }}>{t("ab_ai_assistant.phone_number.saved")} +{savedPhoneNumber}</Typography.Text>
                                    </Row>
                                )}
                                <Row style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                    <InputNumber addonBefore={selectPrefix} value={phoneNumber} onChange={(value) => setPhoneNumber(value)} placeholder={"330300330"} />
                                    <Button type="primary" loading={phoneNumberLoading} onClick={savePhoneNumber} style={{ marginLeft: "10px" }}>{t("ab_ai_assistant.phone_number.save")}</Button>
                                </Row>
                                <Row style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "20px" }}>
                                    <Typography.Text style={{ fontSize: "20px" }}>{t("ab_ai_assistant.phone_number.info")} +39 351 573 4215</Typography.Text>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </>
            )}

        </Row></>
    );
};

export { AB_AI_Assistant };
